import { z } from "zod";

import { getApiKey } from "./addin-auth";
import { getEnv } from "./envManager";
import { safeFetch } from "./safe-fetch";

const PROPOSAL_REDLINE_ENGINE_FLAG = "proposal_redline_engine";

/**
 * `featureFlags` is the web's merge of the org's flags over the signed-in user's
 * own row, the same merge the core applies when it decides which engine writes
 * a card, so the add-in and the core agree on whether a text card can be applied.
 */
const meAccessSchema = z.object({
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
    authorName: z.string().nullish(),
    featureFlags: z
        .object({ [PROPOSAL_REDLINE_ENGINE_FLAG]: z.boolean().default(false) })
        .default({ [PROPOSAL_REDLINE_ENGINE_FLAG]: false }),
});

export interface ProposalEngineAccessState {
    signedIn: boolean;
    enabled: boolean;
    authorName: string | null;
}

const SIGNED_OUT: ProposalEngineAccessState = { signedIn: false, enabled: false, authorName: null };

export class ProposalEngineAccess {
    private static cached: { apiKey: string; state: Promise<ProposalEngineAccessState> } | null = null;

    static current(): Promise<ProposalEngineAccessState> {
        const apiKey = getApiKey();
        /**
         * WHEN there is no API key
         *   CONSEQUENCE
         *     Return the signed-out state immediately.
         *
         * WHY
         *   Skip the /me check until the user has authenticated.
         */
        if (!apiKey) return Promise.resolve(SIGNED_OUT);
        const cachedAccessEntryExists = ProposalEngineAccess.cached !== null;
        const cachedAccessMatchesApiKey = (): boolean => {
            if (!cachedAccessEntryExists) return false;
            return ProposalEngineAccess.cached.apiKey === apiKey;
        };
        /**
         * WHEN the cached entry is for this API key
         *   CONSEQUENCE
         *     Return the cached state promise.
         *
         * WHY
         *   Reuse the in-flight or completed /me lookup for one signed-in user
         *   so repeated callers do not fire duplicate requests.
         */
        if (cachedAccessMatchesApiKey()) return ProposalEngineAccess.cached.state;
        /**
         * WHEN the lookup fails rather than answering
         *   CONSEQUENCE
         *     Answer signed-out for this caller and forget the entry, so the next
         *     caller asks again.
         *
         * WHY
         *   A cached failure never expires and is keyed only by the API key, so one
         *   transient /me error — a 500, or a 401 during a token refresh, neither of
         *   which safeFetch retries — pinned `enabled: false` for the life of the
         *   session. Every text remedy then failed with "the proposal engine, which is
         *   turned off for this account", telling a user whose account has the flag
         *   that it does not.
         *
         * WHY the identity test
         *   A later caller with a new API key may already have replaced the entry;
         *   clearing it unconditionally would throw away that newer lookup.
         */
        const forgetThisEntryOnFailure = (): ProposalEngineAccessState => {
            if (ProposalEngineAccess.cached?.state === state) ProposalEngineAccess.cached = null;
            return SIGNED_OUT;
        };
        const state: Promise<ProposalEngineAccessState> = fetch(apiKey).catch(forgetThisEntryOnFailure);
        ProposalEngineAccess.cached = { apiKey, state };
        return state;
        async function fetch(apiKey: string): Promise<ProposalEngineAccessState> {
            const webappBaseUrl = getEnv("WEBAPP_API_BASE_URL");
            /**
             * WHEN WEBAPP_API_BASE_URL is not configured
             *   CONSEQUENCE
             *     Return SIGNED_OUT.
             *
             * WHY
             *   There is no /me endpoint to query for the current user's access.
             */
            if (!webappBaseUrl) return SIGNED_OUT;
            const response = await safeFetch(`${webappBaseUrl}/api/auth/me`, { headers: { Authorization: `Bearer ${apiKey}` } });
            /**
             * WHEN the /me request does not succeed
             *   CONSEQUENCE
             *     Return SIGNED_OUT.
             *
             * WHY
             *   Only a successful identity check can confirm signed-in access.
             */
            if (!response.ok) return SIGNED_OUT;
            const me = meAccessSchema.parse(await response.json());
            const authorName = me.authorName ?? ([me.firstName, me.lastName].filter(Boolean).join(" ") || null);
            return { signedIn: true, enabled: me.featureFlags[PROPOSAL_REDLINE_ENGINE_FLAG], authorName };
        }
    }
}
